import { useState } from 'react'
import { useNavigate, useSearchParams, Link } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Eye, EyeOff, Loader2, AlertCircle, CheckCircle2 } from 'lucide-react'
import { ROUTES } from '@/router/routes'
import { authApi } from '@/modules/auth/api/auth.api'

const schema = z
  .object({
    password: z
      .string()
      .min(8, 'Minimum 8 caractères')
      .regex(/[A-Z]/, 'Au moins une majuscule')
      .regex(/[0-9]/, 'Au moins un chiffre')
      .regex(/[^A-Za-z0-9]/, 'Au moins un caractère spécial'),
    confirmPassword: z.string(),
  })
  .refine((d) => d.password === d.confirmPassword, {
    message: 'Les mots de passe ne correspondent pas',
    path: ['confirmPassword'],
  })

type FormData = z.infer<typeof schema>

export default function ResetPasswordPage() {
  const [searchParams] = useSearchParams()
  const navigate = useNavigate()
  const token = searchParams.get('token') ?? ''

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirm, setShowConfirm] = useState(false)
  const [success, setSuccess] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [loading, setLoading] = useState(false)

  const {
    register,
    handleSubmit,
    watch,
    formState: { errors },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
  })

  const password = watch('password', '')

  const strength = (() => {
    let s = 0
    if (password.length >= 8) s++
    if (/[A-Z]/.test(password)) s++
    if (/[0-9]/.test(password)) s++
    if (/[^A-Za-z0-9]/.test(password)) s++
    if (password.length >= 12) s++
    return s
  })()
  const strengthColor = [
    '',
    'bg-red-500',
    'bg-orange-500',
    'bg-yellow-500',
    'bg-green-500',
    'bg-[#10B981]',
  ]
  const strengthLabel = ['', 'Faible', 'Faible', 'Moyen', 'Fort', 'Très fort']

  const onSubmit = async (data: FormData) => {
    if (!token) {
      setError('Token manquant ou invalide.')
      return
    }
    setLoading(true)
    setError(null)
    try {
      await authApi.resetPassword(token, data.password)
      setSuccess(true)
      setTimeout(() => navigate(ROUTES.LOGIN), 2500)
    } catch (err: any) {
      setError(err?.response?.data?.message ?? 'Lien expiré ou invalide.')
    } finally {
      setLoading(false)
    }
  }

  if (!token)
    return (
      <div className="min-h-screen bg-[var(--surface-2)] flex items-center justify-center p-4">
        <div className="bg-[#FEF2F2] border border-[rgba(239,68,68,0.35)] rounded-xl p-6 text-center max-w-2xl">
          <AlertCircle className="w-8 h-8 text-[#DC2626] mx-auto mb-3" />
          <p className="text-red-300 font-medium">Lien de réinitialisation invalide ou expiré.</p>
          <Link
            to={ROUTES.FORGOT_PASSWORD}
            className="text-[#4338CA] hover:text-indigo-300 text-sm mt-3 inline-block"
          >
            Demander un nouveau lien
          </Link>
        </div>
      </div>
    )

  return (
    <div className="min-h-screen bg-[var(--surface-2)] flex items-center justify-center p-4">
      <div className="w-full">
        <div className="text-center mb-8">
          <div className="w-12 h-12 bg-[#6366F1] rounded-xl flex items-center justify-center mx-auto mb-4">
            <span className="text-[var(--brand-dark)] font-bold text-lg">N</span>
          </div>
          <h1 className="text-2xl font-bold text-[var(--brand-dark)]">Nouveau mot de passe</h1>
          <p className="text-[var(--tx3)] text-sm mt-1">
            Choisissez un mot de passe sécurisé pour votre compte
          </p>
        </div>

        {success ? (
          <div className="bg-[#10B981]/10 border border-emerald-500/30 rounded-xl p-6 text-center">
            <CheckCircle2 className="w-8 h-8 text-[#059669] mx-auto mb-3" />
            <p className="text-emerald-300 font-medium">Mot de passe réinitialisé !</p>
            <p className="text-[var(--tx3)] text-sm mt-1">Redirection vers la connexion…</p>
          </div>
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {error && (
              <div className="flex items-center gap-2 bg-[#FEF2F2] border border-[rgba(239,68,68,0.35)] rounded-xl p-3">
                <AlertCircle className="w-4 h-4 text-[#DC2626] shrink-0" />
                <p className="text-red-300 text-sm">{error}</p>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-[var(--tx2)] mb-1.5">
                Nouveau mot de passe
              </label>
              <div className="relative">
                <input
                  {...register('password')}
                  type={showPassword ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl px-4 py-2.5 pr-10 text-[var(--brand-dark)] placeholder-[var(--tx3)] focus:outline-none focus:border-indigo-500 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tx3)] hover:text-[var(--brand-dark)]"
                >
                  {showPassword ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {password && (
                <div className="mt-2">
                  <div className="flex gap-1 mb-1">
                    {[1, 2, 3, 4, 5].map((i) => (
                      <div
                        key={i}
                        className={`h-1 flex-1 rounded-full transition-colors ${i <= strength ? strengthColor[strength] : 'bg-[var(--surface-2)]'}`}
                      />
                    ))}
                  </div>
                  <p className="text-xs text-[var(--tx3)]">
                    Force : <span className="text-[var(--brand-dark)]">{strengthLabel[strength]}</span>
                  </p>
                </div>
              )}
              {errors.password && (
                <p className="text-[#DC2626] text-xs mt-1">{errors.password.message}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-[var(--tx2)] mb-1.5">
                Confirmer le mot de passe
              </label>
              <div className="relative">
                <input
                  {...register('confirmPassword')}
                  type={showConfirm ? 'text' : 'password'}
                  placeholder="••••••••"
                  className="w-full bg-[var(--surface-2)] border border-[var(--bd)] rounded-xl px-4 py-2.5 pr-10 text-[var(--brand-dark)] placeholder-[var(--tx3)] focus:outline-none focus:border-indigo-500 text-sm"
                />
                <button
                  type="button"
                  onClick={() => setShowConfirm(!showConfirm)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[var(--tx3)] hover:text-[var(--brand-dark)]"
                >
                  {showConfirm ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>
              {errors.confirmPassword && (
                <p className="text-[#DC2626] text-xs mt-1">{errors.confirmPassword.message}</p>
              )}
            </div>

            <ul className="space-y-1 text-xs text-[var(--tx3)]">
              {[
                { test: password.length >= 8, label: '8 caractères minimum' },
                { test: /[A-Z]/.test(password), label: 'Une majuscule' },
                { test: /[0-9]/.test(password), label: 'Un chiffre' },
                { test: /[^A-Za-z0-9]/.test(password), label: 'Un caractère spécial' },
              ].map(({ test, label }) => (
                <li
                  key={label}
                  className={`flex items-center gap-2 ${test ? 'text-[#059669]' : ''}`}
                >
                  <span>{test ? '✓' : '○'}</span>
                  {label}
                </li>
              ))}
            </ul>

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-[#6366F1] hover:bg-indigo-500 disabled:opacity-50 text-[var(--brand-dark)] font-medium py-2.5 rounded-xl transition-colors flex items-center justify-center gap-2 text-sm"
            >
              {loading && <Loader2 className="w-4 h-4 animate-spin" />}
              {loading ? 'Enregistrement…' : 'Enregistrer le mot de passe'}
            </button>

            <p className="text-center text-sm text-[var(--tx3)]">
              <Link to={ROUTES.LOGIN} className="text-[#4338CA] hover:text-indigo-300">
                Retour à la connexion
              </Link>
            </p>
          </form>
        )}
      </div>
    </div>
  )
}
